// ...existing code...
import React, { ChangeEvent, DragEvent, useEffect, useRef, useState } from 'react';
import handleUpload from '../services/HandleUpload';
import handleValidation from '../services/HandleValidation';
import './FileUpload.css';
import { v4 as uuidv4 } from 'uuid';
import UploadFileCard from './UploadFileCard';
import FillTableCard from './FillTableCard';
import { useToast } from '../layout/Layout';
import Papa from 'papaparse';

export interface FileUploadProps {
    selectedDC: string;
    tabType?: string;
    onUpload: (files: File[]) => void;
    onUploadFilTable: (filesFillTable: File[]) => void;
    onClear?: boolean;
    userId?: string;
}

const FileUpload = (props: FileUploadProps) => {
    // Get toast functions
    const { showSuccess, showError, showWarning } = useToast();
    
    // Per-tab state
    const activeTab = props.tabType || 'XDOCK';
    const [filesByTab, setFilesByTab] = useState<{ [key: string]: File[] }>({});
    const [filesFillTableByTab, setFilesFillTableByTab] = useState<{ [key: string]: File[] }>({});
    const [isFileValidatedByTab, setIsFileValidatedByTab] = useState<{ [key: string]: boolean }>({});
    const [isOpen, setOpen] = useState<boolean>(true);
    const [isSecondCardOpen, setSecondCardOpen] = useState<boolean>(true);
    const [fillTableCardOpen, setFillCardOpen] = useState<boolean>(true);   
    const allowedFileTypes = '.csv,text/csv,application/csv';
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const inputRefUpload = useRef<HTMLInputElement>(null); // Ref for the "Upload File" input
    const inputRefFillTable = useRef<HTMLInputElement>(null); // Ref for the "Fill Table" input
    const [alertMessage, setAlertMessage] = useState<string | null>(null); // Alert message state for Snackbar
    const [alertSeverity, setAlertSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
    const [alertMessageUpload, setAlertMessageUpload] = useState<string | null>(null);
    const [alertSeverityUpload, setAlertSeverityUpload] = useState<'success' | 'error'>('success');
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [uploadResponse, setUploadResponse] = useState<any>(null);
    const [uploadRowCount, setUploadRowCount] = useState(0);
    const fillTablePopulateButton = `Upload ${activeTab} File & Populate Table`;
    const labelDateKey = 'LatestDeliveryDateTime';

    // Per-tab state getters/setters (useCallback for stable references)
    const files = filesByTab[activeTab] || [];
    const setFiles = React.useCallback((newFiles: File[] | ((prev: File[]) => File[])) => {
        setFilesByTab(prev => ({
            ...prev,
            [activeTab]: typeof newFiles === 'function' ? newFiles(prev[activeTab] || []) : newFiles,
        }));
    }, [activeTab]);
    const filesFillTable = filesFillTableByTab[activeTab] || [];
    const setFilesFillTable = React.useCallback((newFiles: File[] | ((prev: File[]) => File[])) => {
        setFilesFillTableByTab(prev => ({
            ...prev,
            [activeTab]: typeof newFiles === 'function' ? newFiles(prev[activeTab] || []) : newFiles,
        }));
    }, [activeTab]);
    const isFileValidated = isFileValidatedByTab[activeTab] || false;
    const setIsFileValidated = React.useCallback((value: boolean) => {
        setIsFileValidatedByTab(prev => ({
            ...prev,
            [activeTab]: value,
        }));
    }, [activeTab]);

    // Clear only current tab's state when tabType changes
useEffect(() => {
    setOpen(true);
    setSecondCardOpen(true);
    setFillCardOpen(true);
    if (inputRefUpload.current) inputRefUpload.current.value = '';
    if (inputRefFillTable.current) inputRefFillTable.current.value = '';
}, [activeTab]);

    const onDrop = (e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (filesFillTable.length > 0 || rows.some((row) => Object.entries(row).some(([key, value]) => key !== 'tempKey' && value !== '' && value !== null))) {
            showError('Please finish the fill/edit table task before proceeding with the Upload File operation');
            return; // Prevent further execution
        }
        const newFiles: File[] = Array.from(e.dataTransfer.files);
        
        // More robust CSV file validation for drag and drop
        const csvFiles = newFiles.filter(file => {
            const isCSVType = file.type === "text/csv" || file.type === "application/csv" || file.type === "";
            const isCSVExtension = file.name.toLowerCase().endsWith(".csv");
            return isCSVType && isCSVExtension;
        });
        
        if (csvFiles.length === 0) {
            const fileTypes = newFiles.map(f => f.name.split('.').pop()?.toUpperCase() || 'unknown').join(', ');
            showError(`Unsupported file format(s): ${fileTypes}. Only CSV files are allowed for upload.`);
            return;
        }

        // Check if some files were filtered out (mixed file types dropped)
        if (csvFiles.length < newFiles.length) {
            const rejectedCount = newFiles.length - csvFiles.length;
            const rejectedFiles = newFiles.filter(file => !csvFiles.includes(file));
            const rejectedTypes = rejectedFiles.map(f => f.name.split('.').pop()?.toUpperCase() || 'unknown').join(', ');
            
            showWarning(`${rejectedCount} file(s) rejected (${rejectedTypes} format). Only CSV files are accepted. ${csvFiles.length} CSV file(s) will be processed.`);
        }

        setFiles((prevFiles) => {
            const updatedFiles = [...prevFiles, ...csvFiles];
            props.onUpload(updatedFiles);
            return updatedFiles;
        });
        setIsFileValidated(false);
    };

    const onUpload = (event: ChangeEvent<HTMLInputElement>): void => {
        if (event.target.files && event.target.files.length > 0) {
            const newFiles = Array.from(event.target.files);
            
            // More robust CSV file validation
            const csvFiles = newFiles.filter((file) => {
                const isCSVType = file.type === "text/csv" || file.type === "application/csv" || file.type === "";
                const isCSVExtension = file.name.toLowerCase().endsWith(".csv");
                return isCSVType && isCSVExtension;
            });

            if (csvFiles.length === 0) {
                const fileTypes = newFiles.map(f => f.name.split('.').pop()?.toUpperCase() || 'unknown').join(', ');
                showError(`Unsupported file format(s): ${fileTypes}. Only CSV files are allowed for upload.`);
                event.target.value = ""; // Clear the input value
                return;
            }

            // Check if some files were filtered out (mixed file types selected)
            if (csvFiles.length < newFiles.length) {
                const rejectedCount = newFiles.length - csvFiles.length;
                const rejectedFiles = newFiles.filter(file => !csvFiles.includes(file));
                const rejectedTypes = rejectedFiles.map(f => f.name.split('.').pop()?.toUpperCase() || 'unknown').join(', ');
                
                showWarning(`${rejectedCount} file(s) rejected (${rejectedTypes} format). Only CSV files are accepted. ${csvFiles.length} CSV file(s) will be processed.`);
            }
            
            setFiles((prevFiles) => {
                const updatedFiles = [...prevFiles, ...csvFiles];
                props.onUpload(updatedFiles); // Pass the valid files to the parent component
                return updatedFiles;
            });
            setIsFileValidated(false);
            event.target.value = "";
        }
    };

    const onDeleteFile = (file: File) => {
        const delfile = files.filter((item) => item !== file);
        setFiles(delfile);
        setIsFileValidated(false);
        if (inputRefUpload.current) {
            inputRefUpload.current.value = '';
        }
    };

    const formatFileSize = (bytes: any) => {
        if (bytes === 0) return '0 Bytes';
        let k = 1000,
            sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
            i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed()) + ' ' + sizes[i];
    };

    const handleCloseSnackbar = () => {
        setAlertMessage(null);
    };

    const resetValues = () => {
        setFiles([]);
        setFilesFillTable([]);
        setIsFileValidated(false);
        if (inputRefFillTable.current) {
            inputRefFillTable.current.value = '';
        }
    };

    const handleProceed = async () => {
        setShowConfirmDialog(false);
        // Call upload again with isProceed = true using stored row count
        handleUpload(
            props.selectedDC,
            activeTab,
            resetValues,
            files,
            uploadRowCount,
            setIsUploading,
            setAlertMessage,
            setAlertSeverity,
            showSuccess,
            showError,
            undefined, // No callback needed
            true, // isProceed = true
            props.userId,
            new Date().toISOString()
        );
        setUploadResponse(null);
    };

    const handleCancel = () => {
        setShowConfirmDialog(false);
        setUploadResponse(null);
    };

    const onValidateClick = () => {
        handleValidation(
            props.selectedDC,
            activeTab,
            setIsFileValidated,
            files,
            setIsLoading,
            setAlertMessage,
            setAlertSeverity, 
            showSuccess, 
            showError,
            (waveData) => {
                setUploadResponse(waveData);
                setShowConfirmDialog(true);
            }
        );
    };

    const onUploadClick = async () => {
        // Count total rows from all CSV files, excluding rows where all fields are empty
        let totalRowCount = 0;
        
        try {
            for (const file of files) {
                const text = await file.text();
                const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
                const nonEmptyRows = (parsed.data as Record<string, string>[]).filter(row =>
                    Object.values(row).some(v => v && v.trim() !== '')
                );
                totalRowCount += nonEmptyRows.length;
            }
        } catch (error) {
            console.error('Error parsing CSV files for row count:', error);
            totalRowCount = 0; // Fallback if parsing fails
        }
        
        setUploadRowCount(totalRowCount); // Store row count for later use
        
        // Define a callback to handle the wave running scenario
        const handleWaveRunning = (waveData: any) => {
          setUploadResponse(waveData); // Store the API response
          setShowConfirmDialog(true);  // Show the confirmation dialog
        };

        // Initial call with isProceed = false
        handleUpload(
            props.selectedDC,
            activeTab,
            resetValues,
            files,
            totalRowCount,
            setIsUploading,
            setAlertMessage,
            setAlertSeverity,
            showSuccess,
            showError,
            handleWaveRunning, // Pass the callback for the wave running case
            false, // isProceed = false
            props.userId,
            new Date().toISOString()
        );
    };

    // Column mappings for different tab types
    // Note: Frontend uses display-friendly field names that are transformed to backend property names during upload
    // The transformation happens in HandleJSONUpload.tsx and HandleJSONValidation.tsx to match LoadGroupBean properties
    const columnMap: Record<string, { key: string; label: string }[]> = {
        XDOCK: [
            { key: 'OrderGroupCode', label: 'Order Group Code' },
            { key: 'OrderCode', label: 'Order Code' },
            { key: 'SourceLocationCode', label: 'Source Location Code' },
            { key: 'DestinationLocationCode', label: 'Destination Location Code' },
            { key: 'CommodityCode', label: 'Commodity Code' },
            { key: 'Weight', label: 'Weight' },
            { key: 'Cubes', label: 'Cubes' },
            { key: 'Cases', label: 'Cases' },
            { key: 'Pallets', label: 'Pallets' },
            { key: 'EarliestDeliveryDateTime', label: 'Earliest Delivery Date Time' },
            { key: 'LatestDeliveryDateTime', label: 'Latest Delivery Date Time' },
            { key: 'ReferenceID', label: 'Reference ID' },
            { key: 'Comments', label: 'Comments' },
            { key: 'PacketCode', label: 'Packet Code' },
            { key: 'CrossDockForLocationCode', label: 'Cross Dock For Location Code' },
            { key: 'CrossDockSequenceNumber', label: 'Cross Dock Sequence Number' },
            { key: 'CrossDockTrailerGroup', label: 'Cross Dock Trailer Group' }
        ],
        IN02: [
            { key: 'Facility', label: 'Facility' },
            { key: 'Route', label: 'Route' },
            { key: 'Shipment ID', label: 'Shipment ID' },
            { key: 'Stop #', label: 'Stop #' },
            { key: 'Shipment Dispatch Date', label: 'Shipment Dispatch Date' },
            { key: 'Stop Destination Facility', label: 'Stop Destination Facility' },
            { key: 'Wave Group', label: 'Wave Group' },
            { key: 'Trailer Request Sent', label: 'Trailer Request Sent' },
            { key: 'Planned Door', label: 'Planned Door' },
            { key: 'Secondary Planned Door', label: 'Secondary Planned Door' },
            { key: 'Tertiary Planned Door', label: 'Tertiary Planned Door' },
            { key: 'Primary Staging Location', label: 'Primary Staging Location' },
            { key: 'Secondary Staging Location', label: 'Secondary Staging Location' },
            { key: 'Tertiary Staging Location', label: 'Tertiary Staging Location' },
            { key: 'Load Group FRZ', label: 'Load Group FRZ' },
            { key: 'Load Group GRO', label: 'Load Group GRO' },
            { key: 'Load Group PER', label: 'Load Group PER' },
            { key: 'Load Group GML', label: 'Load Group GML' },
            { key: 'Load Group PRO', label: 'Load Group PRO' }
        ]
    };

    // Select columns based on tabType, default to XDOCK if tabType is not found
    const selectedColumns = columnMap[props.tabType || 'XDOCK'] || columnMap['XDOCK'];

const [isTableVisible] = useState(false);

// Generate initial row structure based on selected columns
const generateInitialRows = (columns: any[]) => {
    const initialRowData: any = { tempKey: uuidv4() };
    columns.forEach(col => {
        initialRowData[col.key] = '';
    });
    return initialRowData;
};

const [rows] = useState(() => 
    Array.from({ length: 25 }, () => generateInitialRows(selectedColumns))
);

const onUploadFilTable = (event: ChangeEvent<HTMLInputElement>): void => {
    if (event.target.files && event.target.files.length > 0) {
        const newFilesFillTable = Array.from(event.target.files);
        
        // More robust CSV file validation
        const csvFiles = newFilesFillTable.filter((file) => {
            const isCSVType = file.type === "text/csv" || file.type === "application/csv" || file.type === "";
            const isCSVExtension = file.name.toLowerCase().endsWith(".csv");
            return isCSVType && isCSVExtension;
        });

        if (csvFiles.length === 0) {
            const fileTypes = newFilesFillTable.map(f => f.name.split('.').pop()?.toUpperCase() || 'unknown').join(', ');
            showError(`Unsupported file format(s): ${fileTypes}. Only CSV files are allowed for Fill Table.`);
            event.target.value = ""; // Clear the input value
            return;
        }

            // Check if some files were filtered out (mixed file types selected)
            if (csvFiles.length < newFilesFillTable.length) {
                const rejectedCount = newFilesFillTable.length - csvFiles.length;
                const rejectedFiles = newFilesFillTable.filter(file => !csvFiles.includes(file));
                const rejectedTypes = rejectedFiles.map(f => f.name.split('.').pop()?.toUpperCase() || 'unknown').join(', ');
                
                showWarning(`${rejectedCount} file(s) rejected (${rejectedTypes} format). Only CSV files are accepted. ${csvFiles.length} CSV file(s) will be processed.`);
            }        setFilesFillTable((prevFiles) => {
            const updatedFiles = [...prevFiles, ...csvFiles];
            props.onUploadFilTable(updatedFiles); // Pass the valid files to the parent component
            return updatedFiles;
        });
        setIsFileValidated(false);
        event.target.value = "";
    }
};
const handleCloseSnackbarUpload = (event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
        return; // Ignore clickaway events
    }
    event.stopPropagation();
    setAlertMessageUpload(null);
};

useEffect(() => {
    if (props.onClear) setFiles([]);
}, [props.onClear, setFiles]);

return (
    <>
        <UploadFileCard
            isTableVisible={isTableVisible}
            inputRefUpload={inputRefUpload}
            files={files}
            setFiles={setFiles}
            onDrop={onDrop}
            tabType={activeTab}
            alertMessageUpload={alertMessageUpload}
            handleCloseSnackbarUpload={handleCloseSnackbarUpload}
            isOpen={isOpen}
            setOpen={setOpen}
            alertSeverityUpload={alertSeverityUpload}
            allowedFileTypes={allowedFileTypes}
            filesFillTable={filesFillTable}
            setAlertMessageUpload={setAlertMessageUpload}
            rows={rows}
            setAlertMessage={setAlertMessage}
            handleCloseSnackbar={handleCloseSnackbar}
            isLoading={isLoading}
            isUploading={isUploading}
            setAlertSeverity={setAlertSeverity}
            setAlertSeverityUpload={setAlertSeverityUpload}
            onDeleteFile={onDeleteFile}
            resetValues={resetValues}
            onUpload={onUpload}
            onValidateClick={onValidateClick}
            onUploadClick={onUploadClick}
            isFileValidated={isFileValidated}
            setIsFileValidated={setIsFileValidated}
            alertMessage={alertMessage}
            onUploadFilTable={onUploadFilTable}
            alertSeverity={alertSeverity}
            props={props}
            formatFileSize={formatFileSize}
            showConfirmDialog={showConfirmDialog}
            uploadResponse={uploadResponse}
            onProceed={handleProceed}
            onCancel={handleCancel}
        />

        <FillTableCard
            filesFillTable={filesFillTable}
            setFilesFillTable={setFilesFillTable}
            selectedDC={props.selectedDC}
            tabType={activeTab}
            isSecondCardOpen={isSecondCardOpen}
            setSecondCardOpen={setSecondCardOpen}
            files={files}
            setOpen={setOpen}
            formatFileSize={formatFileSize}
            fillTablePopulateButton={fillTablePopulateButton}
            columns={
                (selectedColumns?.map((col) =>
                    typeof col === 'string'
                        ? { key: col, name: col }
                        : { key: (col as any).key, name: (col as any).label ?? (col as any).name }
                ) ?? [])
            }
            labelDateKey={labelDateKey}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            fillTableCardOpen={fillTableCardOpen}
            setFillCardOpen={setFillCardOpen}
            userId={props.userId}
        />
    </>
);
};

export default FileUpload;